// src/pages/NotesPage.jsx
import React, { useState, useContext, useEffect, useCallback } from 'react';
import Header from '../components/common/Header';
import Sidebar from '../components/common/Sidebar';
import { ThemeContext } from '../context/ThemeContext';
import notesService from '../services/notesService';
import flashcardService from '../services/flashcardservice';
import { toast } from 'react-toastify';
import debounce from 'lodash/debounce';
import { API_ENDPOINTS } from '../config/api';
import { jsPDF } from 'jspdf';


const NotesPage = () => {
  const { theme } = useContext(ThemeContext);
  const [notes, setNotes] = useState([]);
  const [selectedNote, setSelectedNote] = useState(null);
  const [summary, setSummary] = useState('');
  const [isSummarizing, setIsSummarizing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingContent, setEditingContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [isGeneratingFlashcards, setIsGeneratingFlashcards] = useState(false);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'favorites', 'archived'
  const [clusters, setClusters] = useState([]);
  const [selectedCluster, setSelectedCluster] = useState('');
  const [newClusterName, setNewClusterName] = useState('');
  const [showClusterModal, setShowClusterModal] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'topics'
  const [expandedTopics, setExpandedTopics] = useState({});
  const [showClusterAssignModal, setShowClusterAssignModal] = useState(false);
  const [pendingNoteForCluster, setPendingNoteForCluster] = useState(null);
  const [selectedClusterForNote, setSelectedClusterForNote] = useState('');
  const [newClusterForNote, setNewClusterForNote] = useState('');
  const [exportClusterFilter, setExportClusterFilter] = useState('all'); // 'all', 'summarized', or specific cluster ID

  // Helper function to group notes by clusters
  const groupNotesByTopic = (notesArray) => {
    const grouped = {};
    notesArray.forEach(note => {
      const cluster = note.cluster || 'Unclustered';
      if (!grouped[cluster]) grouped[cluster] = [];
      grouped[cluster].push(note);
    });
    // Sort clusters alphabetically
    return Object.keys(grouped).sort().reduce((obj, key) => {
      obj[key] = grouped[key];
      return obj;
    }, {});
  };

  const toggleTopicExpand = (topic) => {
    setExpandedTopics(prev => ({
      ...prev,
      [topic]: !prev[topic]
    }));
  };

  // Fetch notes on component mount
  useEffect(() => {
    fetchNotes();
    fetchClusters();
  }, []);

  const fetchNotes = async (search = '') => {
    try {
      setLoading(true);
      const params = {};
      if (search) {
        params.search = search;
      }
      if (activeTab === 'favorites') {
        params.favorite = 'true';
      }
      if (activeTab === 'archived') {
        params.archived = 'true';
      }
      if (selectedCluster) {
        params.cluster = selectedCluster;
      }

      const response = await notesService.getAllNotes(params);
      setNotes(response.data.notes);
    } catch (error) {
      toast.error('Failed to fetch notes');
      console.error('Error fetching notes:', error);
    } finally {
      setLoading(false);
    }
  };

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((search) => {
      fetchNotes(search);
    }, 500),
    [activeTab]
  );

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  const handleSummarize = async () => {
    if (!selectedNote) {
      toast.warning('Please select a note to summarize');
      return;
    }

    if (!selectedNote.content || !selectedNote.content.trim()) {
      toast.warning('Selected note is empty. Add content before summarizing.');
      return;
    }
    
    setIsSummarizing(true);
    try {
      const response = await notesService.summarizeNote(selectedNote._id);
      setSummary(response.data.summary);
      
      // Assign the note to "summarized notes" cluster
      try {
        const clusterRes = await notesService.updateCluster(selectedNote._id, 'summarized notes');
        const updatedNote = clusterRes.data?.note || clusterRes.note;
        if (updatedNote) {
          // Update the selected note with the new cluster assignment
          setSelectedNote(updatedNote);
          // Update the notes list
          setNotes(notes.map(n => n._id === updatedNote._id ? updatedNote : n));
        }
      } catch (clusterError) {
        console.error('Failed to assign to summarized notes cluster:', clusterError);
        // Don't fail the whole operation, just log the error
      }
      
      toast.success('Note summarized and assigned to "summarized notes" cluster!');
    } catch (error) {
      if (error.response?.status === 400 && error.response.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.response?.status === 403) {
        toast.error('AI search limit reached. Upgrade to Pro for unlimited searches.');
      } else {
        toast.error('Failed to summarize note');
      }
      console.error('Summarize error:', error);
    } finally {
      setIsSummarizing(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0] || null;
    setSelectedFile(file);
  };

  const handleAssignClusterToNote = async () => {
    if (!pendingNoteForCluster) return;

    try {
      if (selectedClusterForNote) {
        // Assign to existing cluster
        const res = await notesService.updateCluster(pendingNoteForCluster._id, selectedClusterForNote);
        const note = res.data?.note || res.note;
        if (note) {
          setNotes(notes.map(n => n._id === note._id ? note : n));
          setSelectedNote(note);
          toast.success('Note assigned to cluster!');
          await fetchClusters();
        } else {
          toast.error('Failed to assign cluster');
          return;
        }
      } else if (newClusterForNote.trim()) {
        // Create new cluster and assign
        const res = await notesService.updateCluster(pendingNoteForCluster._id, newClusterForNote.trim());
        const note = res.data?.note || res.note;
        if (note) {
          setNotes(notes.map(n => n._id === note._id ? note : n));
          setSelectedNote(note);
          toast.success('Cluster created and note assigned!');
          await fetchClusters();
        } else {
          toast.error('Failed to create and assign cluster');
          return;
        }
      }
      setShowClusterAssignModal(false);
      setPendingNoteForCluster(null);
      setSelectedClusterForNote('');
      setNewClusterForNote('');
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to assign cluster';
      toast.error(message);
      console.error('Cluster assign error:', error);
    }
  };

  // In NotesPage.jsx - Fix the upload handler
const handleUploadCreate = async () => {
  if (!selectedFile) {
    toast.warning('Please select a file to upload');
    return;
  }
  
  setUploading(true);
  try {
    // Call the upload API directly with proper headers
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', selectedFile);
    
    // Use notesService instead of fetch to ensure correct URL
    const response = await notesService.uploadNote(selectedFile);
    
    if (response.status === 'success') {
      const note = response.data.note;
      setNotes([note, ...notes]);
      setSelectedNote(note);
      setSelectedFile(null);
      setSummary('');
      
      // Show cluster assignment modal
      setPendingNoteForCluster(note);
      setShowClusterAssignModal(true);
      toast.success('File uploaded! Please assign it to a cluster.');
    } else {
      toast.error(response.message || 'Upload failed');
    }
  } catch (error) {
    console.error('Upload create error:', error);
    console.error('Error response data:', error.response?.data);
    console.error('Error response status:', error.response?.status);
    console.error('Error response headers:', error.response?.headers);
    const errorMessage = error.response?.data?.message || error.message || 'Failed to upload file';
    toast.error(errorMessage);
  } finally {
    setUploading(false);
  }
};

  const handleUploadReplace = async () => {
    if (!selectedFile || !selectedNote) {
      toast.warning('Please select a note and a file to upload');
      return;
    }
    setUploading(true);
    try {
      const res = await notesService.uploadNote(selectedFile, selectedNote._id);
      const note = res.data.note || res.note;
      if (note) {
        // Update notes list and selected note
        setNotes(notes.map(n => n._id === note._id ? note : n));
        setSelectedNote(note);
        setSelectedFile(null);
        setSummary('');
        toast.success('File uploaded and note content replaced!');
      } else {
        toast.error('Upload did not return an updated note');
      }
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to upload file';
      toast.error(msg);
      console.error('Upload replace error:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleGenerateFlashcards = async () => {
    if (!selectedNote) {
      toast.warning('Please select a note to generate flashcards from');
      return;
    }
    const countStr = window.prompt('How many flashcards to generate?', '5');
    const count = parseInt(countStr, 10) || 5;
    setIsGeneratingFlashcards(true);
    try {
      await flashcardService.generateFromNote(selectedNote._id, count);
      toast.success('Flashcards generated successfully!');
    } catch (error) {
      toast.error('Failed to generate flashcards');
      console.error('Generate flashcards error:', error);
    } finally {
      setIsGeneratingFlashcards(false);
    }
  };

  const handleSaveNote = async () => {
    if (!selectedNote) return;
    
    setSaving(true);
    try {
      const res = await notesService.updateNote(selectedNote._id, {
        content: editingContent
      });
      // Use server's returned note to stay in sync
      const updated = res.data?.note || res.note || null;
      if (updated) {
        setNotes(notes.map(note => note._id === selectedNote._id ? updated : note));
        setSelectedNote(updated);
        setEditingContent(updated.content);
      } else {
        // Fallback to optimistic update
        setNotes(notes.map(note => 
          note._id === selectedNote._id 
            ? { ...note, content: editingContent, updatedAt: new Date().toISOString() }
            : note
        ));
      }
      setHasUnsavedChanges(false);
      toast.success('Note saved successfully!');
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to save note';
      toast.error(msg);
      console.error('Save error:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateNote = async () => {
    try {
      const newNote = {
        title: 'New Note',
        content: 'Start typing your note here...',
        tags: ['new'],
        category: 'personal',
      };
      
      const response = await notesService.createNote(newNote);
      const createdNote = response.data.note;
      setNotes([createdNote, ...notes]);
      setSelectedNote(createdNote);
      setEditingContent(createdNote.content);
      setHasUnsavedChanges(false);
      
      // Show cluster assignment modal
      setPendingNoteForCluster(createdNote);
      setShowClusterAssignModal(true);
      toast.success('New note created! Please assign it to a cluster.');
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to create note';
      toast.error(message);
      console.error('Create error:', error);
    }
  };

  const handleDeleteNote = async (noteId) => {
    // Use window.confirm properly
    if (window.confirm('Are you sure you want to delete this note?')) {
      try {
        await notesService.deleteNote(noteId);
        setNotes(notes.filter(note => note._id !== noteId));
        if (selectedNote && selectedNote._id === noteId) {
          setSelectedNote(null);
          setEditingContent('');
          setSummary('');
        }
        toast.success('Note deleted successfully!');
      } catch (error) {
        toast.error('Failed to delete note');
        console.error('Delete error:', error);
      }
    }
  };

  const handleToggleFavorite = async (noteId) => {
    try {
      await notesService.toggleFavorite(noteId);
      setNotes(notes.map(note => 
        note._id === noteId 
          ? { ...note, isFavorite: !note.isFavorite }
          : note
      ));
    } catch (error) {
      console.error('Toggle favorite error:', error);
    }
  };

  const handleToggleArchive = async (noteId) => {
    try {
      await notesService.toggleArchive(noteId);
      setNotes(notes.map(note => 
        note._id === noteId 
          ? { ...note, isArchived: !note.isArchived }
          : note
      ));
      toast.success('Note archive status updated!');
    } catch (error) {
      toast.error('Failed to update archive status');
      console.error('Archive error:', error);
    }
  };

  const handleExportNotes = () => {
    // Filter notes based on export cluster filter
    let notesToExport = notes;
    let exportFileName = `notes_export_${new Date().toISOString().split('T')[0]}`;

    if (exportClusterFilter === 'summarized') {
      // Export all summarized notes (notes in "summarized notes" cluster)
      notesToExport = notes.filter(note => note.cluster === 'summarized notes');
      exportFileName = `summarized_notes_${new Date().toISOString().split('T')[0]}`;
    } else if (exportClusterFilter !== 'all' && exportClusterFilter) {
      // Export notes from a specific cluster
      const cluster = clusters.find(c => c._id === exportClusterFilter);
      notesToExport = notes.filter(note => note.cluster === exportClusterFilter);
      exportFileName = `${cluster?.name || 'notes'}_${new Date().toISOString().split('T')[0]}`;
    }

    if (notesToExport.length === 0) {
      toast.warning('No notes to export');
      return;
    }

    try {
      // Create a new PDF document
      const doc = new jsPDF();
      let yPosition = 10;
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 10;
      const maxWidth = doc.internal.pageSize.getWidth() - 2 * margin;
      
      // Add title
      doc.setFontSize(16);
      doc.text('Notes Export', margin, yPosition);
      yPosition += 10;
      
      // Add filter info
      let filterInfo = 'All Notes';
      if (exportClusterFilter === 'summarized') {
        filterInfo = 'Summarized Notes';
      } else if (exportClusterFilter !== 'all' && exportClusterFilter) {
        const cluster = clusters.find(c => c._id === exportClusterFilter);
        filterInfo = `Cluster: ${cluster?.name || 'Unknown'}`;
      }
      
      // Add date
      doc.setFontSize(10);
      doc.setTextColor(128, 128, 128);
      doc.text(`${filterInfo} | Exported on: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`, margin, yPosition);
      yPosition += 8;
      
      // Add separator line
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, yPosition, doc.internal.pageSize.getWidth() - margin, yPosition);
      yPosition += 10;
      
      // Process each note
      notesToExport.forEach((note, index) => {
        // Check if we need a new page
        if (yPosition > pageHeight - 20) {
          doc.addPage();
          yPosition = 10;
        }
        
        // Note title
        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0);
        doc.setFont(undefined, 'bold');
        doc.text(`${index + 1}. ${note.title || 'Untitled'}`, margin, yPosition);
        yPosition += 7;
        
        // Note metadata
        doc.setFontSize(9);
        doc.setTextColor(100, 100, 100);
        doc.setFont(undefined, 'normal');
        doc.text(`Created: ${new Date(note.createdAt).toLocaleDateString()}`, margin + 2, yPosition);
        yPosition += 5;
        
        if (note.cluster && note.cluster !== 'Unclustered') {
          doc.text(`Cluster: ${note.cluster}`, margin + 2, yPosition);
          yPosition += 5;
        }
        
        if (note.tags && note.tags.length > 0) {
          doc.text(`Tags: ${note.tags.join(', ')}`, margin + 2, yPosition);
          yPosition += 5;
        }
        
        // Note content
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);
        doc.setFont(undefined, 'normal');
        
        // Split content into multiple lines
        const contentLines = doc.splitTextToSize(
          note.content || 'No content',
          maxWidth - 4
        );
        
        contentLines.forEach(line => {
          if (yPosition > pageHeight - 15) {
            doc.addPage();
            yPosition = 10;
          }
          doc.text(line, margin + 2, yPosition);
          yPosition += 5;
        });
        
        // Add separator between notes
        yPosition += 5;
        if (yPosition <= pageHeight - 20 && index < notesToExport.length - 1) {
          doc.setDrawColor(220, 220, 220);
          doc.line(margin, yPosition, doc.internal.pageSize.getWidth() - margin, yPosition);
          yPosition += 8;
        }
      });
      
      // Save the PDF
      doc.save(`${exportFileName}.pdf`);
      toast.success('Notes exported as PDF successfully!');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to export notes as PDF');
    }
  };

  const handleSelectNote = (note) => {
    // Check for unsaved changes
    if (hasUnsavedChanges && selectedNote) {
      if (window.confirm('You have unsaved changes. Are you sure you want to switch notes?')) {
        setSelectedNote(note);
        setEditingContent(note.content);
        setSummary('');
        setHasUnsavedChanges(false);
      }
    } else {
      setSelectedNote(note);
      setEditingContent(note.content);
      setSummary('');
      setHasUnsavedChanges(false);
    }
  };

  const handleContentChange = (e) => {
    setEditingContent(e.target.value);
    setHasUnsavedChanges(true);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSelectedNote(null);
    setEditingContent('');
    setSummary('');
    setHasUnsavedChanges(false);
    setSelectedCluster(''); // Reset cluster when changing tabs
    fetchNotesWithTab(tab);
  };

  const handleClusterChange = (cluster) => {
    setSelectedCluster(cluster);
    setSelectedNote(null);
    setEditingContent('');
    setSummary('');
    setHasUnsavedChanges(false);
    fetchNotes();
  };

  const handleCreateCluster = async () => {
    if (!newClusterName.trim()) {
      toast.warning('Please enter a cluster name');
      return;
    }

    if (!selectedNote) {
      toast.warning('Please select a note to assign to the cluster');
      return;
    }

    try {
      await notesService.updateCluster(selectedNote._id, newClusterName.trim());
      setNotes(notes.map(note =>
        note._id === selectedNote._id
          ? { ...note, cluster: newClusterName.trim() }
          : note
      ));
      setSelectedNote({ ...selectedNote, cluster: newClusterName.trim() });
      setNewClusterName('');
      setShowClusterModal(false);
      fetchClusters(); // Refresh clusters
      toast.success('Note assigned to cluster successfully!');
    } catch (error) {
      toast.error('Failed to create cluster');
      console.error('Create cluster error:', error);
    }
  };

  const handleAssignCluster = async (clusterId) => {
    if (!selectedNote) {
      toast.warning('Please select a note to assign');
      return;
    }

    try {
      const res = await notesService.updateCluster(selectedNote._id, clusterId);
      const note = res.data?.note || res.note;
      if (note) {
        setNotes(notes.map(n => n._id === note._id ? note : n));
        setSelectedNote(note);
        toast.success('Cluster assigned');
        fetchClusters();
        setShowClusterModal(false);
      } else {
        toast.error('Failed to assign cluster');
      }
    } catch (err) {
      console.error('Assign cluster error', err);
      toast.error('Failed to assign cluster');
    }
  };

  const fetchClusters = async () => {
    try {
      const response = await notesService.getClusters();
      setClusters(response.data.clusters || []);
    } catch (error) {
      console.error('Error fetching clusters:', error);
    }
  };

  const fetchNotesWithTab = async (tab) => {
    try {
      setLoading(true);
      const params = {};
      if (tab === 'favorites') {
        params.favorite = 'true';
      }
      if (tab === 'archived') {
        params.archived = 'true';
      }

      const response = await notesService.getAllNotes(params);
      setNotes(response.data.notes);
    } catch (error) {
      toast.error('Failed to fetch notes');
      console.error('Error fetching notes:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredNotes = notes.filter(note => {
    if (activeTab === 'favorites' && !note.isFavorite) return false;
    if (activeTab === 'archived' && !note.isArchived) return false;
    if (activeTab === 'all' && note.isArchived) return false; // Don't show archived in "all" by default
    return true;
  });

  // Calculate statistics
  const stats = {
    total: notes.length,
    favorites: notes.filter(n => n.isFavorite).length,
    archived: notes.filter(n => n.isArchived).length,
    recent: notes.filter(n => {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return new Date(n.updatedAt) > weekAgo;
    }).length
  };

  return (
    <div className={`notes-page ${theme === 'dark' ? 'dark' : ''}`}>
      <Header />
      <div className="flex">
        <Sidebar />
        <main className="flex-1 p-4 md:p-6">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-6 md:mb-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
                    Notes
                  </h1>
                  <p className="text-gray-600 dark:text-gray-300 mt-1 md:mt-2">
                    Create, edit, and summarize your notes
                  </p>
                </div>
                <div className="flex gap-2 w-full md:w-auto">
                  <button
                    onClick={handleCreateNote}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg transition flex-1 md:flex-none"
                  >
                    + New Note
                  </button>
                  <input
                    id="file-upload-header"
                    type="file"
                    accept=".pdf,.docx,.txt,.md"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setUploading(true);
                        try {
                          const res = await notesService.uploadNote(file);
                          const note = res.data.note || res.note;
                          if (note) {
                            setNotes([note, ...notes]);
                            setSelectedNote(note);
                            setSummary('');
                            toast.success('File uploaded and note created!');
                          } else {
                            toast.error('Upload did not return a note');
                          }
                        } catch (error) {
                          const msg = error.response?.data?.message || 'Failed to upload file';
                          toast.error(msg);
                          console.error('Upload error:', error);
                        } finally {
                          setUploading(false);
                          e.target.value = ''; // Reset file input
                        }
                      }
                    }}
                    className="hidden"
                  />
                  
                </div>
              </div>

              {/* Cluster Modal */}
              {showClusterModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                  <div className="absolute inset-0 bg-black/40" onClick={() => setShowClusterModal(false)} />

                  <div className="relative w-full max-w-2xl mx-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold">Manage Clusters</h3>
                      <button onClick={() => setShowClusterModal(false)} className="text-gray-500 hover:text-gray-700">✕</button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600 mb-2">Existing clusters</p>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {clusters.length === 0 ? (
                            <div className="text-sm text-gray-500">No clusters yet</div>
                          ) : (
                            clusters.map(c => (
                              <div key={c._id} className="flex items-center justify-between p-2 border rounded">
                                <div>
                                  <div className="font-medium">{c._id}</div>
                                  <div className="text-xs text-gray-500">{c.count} notes</div>
                                </div>
                                <div className="flex gap-2">
                                  <button onClick={() => handleAssignCluster(c._id)} className="px-3 py-1 bg-blue-600 text-white rounded text-sm">Assign</button>
                                  <button onClick={() => handleAssignCluster('')} className="px-3 py-1 bg-gray-100 rounded text-sm">Remove</button>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>

                      <div>
                        <p className="text-sm text-gray-600 mb-2">Create new cluster</p>
                        <input value={newClusterName} onChange={(e) => setNewClusterName(e.target.value)} placeholder="e.g. Physics, Meetings" className="w-full p-2 border rounded mb-2" />
                        <div className="flex gap-2">
                          <button onClick={handleCreateCluster} className="px-4 py-2 bg-purple-600 text-white rounded">Create & Assign</button>
                          <button onClick={() => { setNewClusterName(''); }} className="px-4 py-2 border rounded">Clear</button>
                        </div>
                        <p className="text-xs text-gray-500 mt-3">Tip: clusters are simple topic labels to group related notes.</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Cluster Assignment Modal */}
              {showClusterAssignModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                  <div className="absolute inset-0 bg-black/40" onClick={() => setShowClusterAssignModal(false)} />

                  <div className="relative w-full max-w-md mx-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold">Assign to Cluster</h3>
                      <button 
                        onClick={() => {
                          setShowClusterAssignModal(false);
                          setPendingNoteForCluster(null);
                          setSelectedClusterForNote('');
                          setNewClusterForNote('');
                        }} 
                        className="text-gray-500 hover:text-gray-700"
                      >
                        ✕
                      </button>
                    </div>

                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      Note: <strong>{pendingNoteForCluster?.title || 'Untitled'}</strong>
                    </p>

                    {/* Existing Clusters */}
                    {clusters.length > 0 && (
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Assign to existing cluster:
                        </label>
                        <select
                          value={selectedClusterForNote}
                          onChange={(e) => {
                            setSelectedClusterForNote(e.target.value);
                            setNewClusterForNote('');
                          }}
                          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                          <option value="">-- Select a cluster --</option>
                          {clusters.map(cluster => (
                            <option key={cluster._id} value={cluster._id}>
                              {cluster._id} ({cluster.count} notes)
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Create New Cluster */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Or create a new cluster:
                      </label>
                      <input
                        type="text"
                        value={newClusterForNote}
                        onChange={(e) => {
                          setNewClusterForNote(e.target.value);
                          setSelectedClusterForNote('');
                        }}
                        placeholder="e.g. Physics, Meetings, Python"
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      <button
                        onClick={handleAssignClusterToNote}
                        disabled={!selectedClusterForNote && !newClusterForNote.trim()}
                        className="flex-1 px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Assign & Continue
                      </button>
                      <button
                        onClick={() => {
                          setShowClusterAssignModal(false);
                          setPendingNoteForCluster(null);
                          setSelectedClusterForNote('');
                          setNewClusterForNote('');
                        }}
                        className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white font-medium rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition"
                      >
                        Skip
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
              {/* Left Column - Notes List & Editor */}
              <div className="lg:col-span-2 space-y-4 md:space-y-6">
                {/* Search Bar */}
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search notes..."
                    value={searchTerm}
                    onChange={handleSearch}
                    className="w-full p-3 md:p-4 pl-10 md:pl-12 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="absolute left-3 md:left-4 top-3 md:top-4 text-gray-400">🔍</span>
                </div>

                {/* Tabs and Clusters */}
                <div className="space-y-4">
                  <div className="flex border-b border-gray-200 dark:border-gray-700">
                    {['all', 'favorites', 'archived'].map(tab => (
                      <button
                        key={tab}
                        onClick={() => handleTabChange(tab)}
                        className={`px-4 py-2 font-medium capitalize ${
                          activeTab === tab
                            ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400'
                            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                        }`}
                      >
                        {tab} {tab === 'all' ? `(${stats.total})` : tab === 'favorites' ? `(${stats.favorites})` : `(${stats.archived})`}
                      </button>
                    ))}
                  </div>

                  {/* Clusters Filter */}
                  {clusters.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      <span className="text-sm text-gray-600 dark:text-gray-300 mr-2">Clusters:</span>
                      <button
                        onClick={() => handleClusterChange('')}
                        className={`px-3 py-1 text-xs rounded-full ${
                          selectedCluster === ''
                            ? 'bg-blue-500 text-white'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        All
                      </button>
                      {clusters.map(cluster => (
                        <button
                          key={cluster._id}
                          onClick={() => handleClusterChange(cluster._id)}
                          className={`px-3 py-1 text-xs rounded-full ${
                            selectedCluster === cluster._id
                              ? 'bg-blue-500 text-white'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                          }`}
                        >
                          {cluster._id} ({cluster.count})
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Notes List */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4 md:p-6">
                  {/* View Mode Toggle */}
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="font-bold text-lg text-gray-900 dark:text-white">Notes</h2>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setViewMode('list')}
                        className={`px-3 py-1 text-sm rounded-lg transition ${
                          viewMode === 'list'
                            ? 'bg-blue-500 text-white'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        📋 List
                      </button>
                      <button
                        onClick={() => setViewMode('topics')}
                        className={`px-3 py-1 text-sm rounded-lg transition ${
                          viewMode === 'topics'
                            ? 'bg-blue-500 text-white'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        🏷️ Topics
                      </button>
                    </div>
                  </div>

                  <div className="space-y-3 md:space-y-4 max-h-[400px] overflow-y-auto pr-2">
                    {loading && notes.length === 0 ? (
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                        <p className="mt-2 text-gray-600 dark:text-gray-300">Loading notes...</p>
                      </div>
                    ) : filteredNotes.length === 0 ? (
                      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        {activeTab === 'all' ? 'No notes yet. Create your first note!' :
                         activeTab === 'favorites' ? 'No favorite notes yet.' :
                         'No archived notes yet.'}
                      </div>
                    ) : viewMode === 'list' ? (
                      filteredNotes.map(note => (
                        <div
                          key={note._id}
                          onClick={() => handleSelectNote(note)}
                          className={`p-3 md:p-4 rounded-lg border cursor-pointer transition-all ${
                            selectedNote?._id === note._id
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                              : 'border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-700'
                          }`}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-bold text-gray-900 dark:text-white">
                                {note.title || 'Untitled Note'}
                              </h3>
                              {note.isFavorite && (
                                <span className="text-yellow-500 text-sm">★</span>
                              )}
                              {note.isArchived && (
                                <span className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded">Archived</span>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <span className="text-xs md:text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                {new Date(note.updatedAt).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                          <p className="text-gray-600 dark:text-gray-300 mt-2 text-sm md:text-base line-clamp-2">
                            {note.summary || note.content?.substring(0, 100) || 'No content yet'}
                            {note.content?.length > 100 && '...'}
                          </p>
                          <div className="flex justify-between items-center mt-3">
                            <div className="flex gap-1 md:gap-2 flex-wrap">
                              {note.tags?.slice(0, 3).map(tag => (
                                <span 
                                  key={tag} 
                                  className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 rounded"
                                >
                                  {tag}
                                </span>
                              ))}
                              {note.tags?.length > 3 && (
                                <span className="px-2 py-1 text-xs text-gray-500">+{note.tags.length - 3}</span>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleFavorite(note._id);
                                }}
                                className={`p-1 ${note.isFavorite ? 'text-yellow-500' : 'text-gray-400'}`}
                                title={note.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                              >
                                ★
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteNote(note._id);
                                }}
                                className="text-red-500 hover:text-red-700 p-1"
                                title="Delete note"
                              >
                                ×
                              </button>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      // Topics View
                      Object.entries(groupNotesByTopic(filteredNotes)).map(([topic, topicNotes]) => (
                        <div key={topic} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                          <button
                            onClick={() => toggleTopicExpand(topic)}
                            className="w-full px-4 py-3 bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 hover:from-indigo-100 hover:to-blue-100 dark:hover:from-indigo-900/30 dark:hover:to-blue-900/30 transition flex items-center justify-between"
                          >
                            <div className="flex items-center gap-3">
                              <span className="text-lg">{expandedTopics[topic] ? '▼' : '▶'}</span>
                              <span className="font-bold text-gray-900 dark:text-white">{topic}</span>
                              <span className="text-sm text-gray-600 dark:text-gray-400">({topicNotes.length})</span>
                            </div>
                          </button>
                          
                          {expandedTopics[topic] && (
                            <div className="space-y-2 p-4 bg-white dark:bg-gray-700/50">
                              {topicNotes.map(note => (
                                <div
                                  key={note._id}
                                  onClick={() => handleSelectNote(note)}
                                  className={`p-3 rounded-lg border cursor-pointer transition-all ${
                                    selectedNote?._id === note._id
                                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                                      : 'border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-700'
                                  }`}
                                >
                                  <div className="flex justify-between items-start mb-2">
                                    <h4 className="font-bold text-gray-900 dark:text-white flex-1">
                                      {note.title || 'Untitled'}
                                    </h4>
                                    <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap ml-2">
                                      {new Date(note.updatedAt).toLocaleDateString()}
                                    </span>
                                  </div>
                                  <p className="text-gray-600 dark:text-gray-300 text-sm line-clamp-1">
                                    {note.summary || note.content?.substring(0, 60) || 'No content'}
                                  </p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Upload Section */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4 md:p-6 mb-4 md:mb-6">
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white mb-3 md:mb-4">
                    Upload New Note
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-3 md:mb-4 text-sm md:text-base">
                    Upload a file to create a new note
                  </p>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Select file (PDF, DOCX, DOC, PPTX, TXT, MD)</label>
                      <input
                        type="file"
                        accept=".pdf,.docx,.doc,.pptx,.ppt,.txt,.md"
                        onChange={(e) => handleFileSelect(e)}
                        className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                    <button
                      onClick={handleUploadCreate}
                      disabled={!selectedFile || uploading}
                      className="w-full py-2 md:py-3 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm md:text-base"
                    >
                      {uploading ? 'Uploading...' : '📤 Upload & Create Note'}
                    </button>
                  </div>
                </div>

                {/* Note Editor (now upload-first: remove typing) */}
                {selectedNote && (
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4 md:p-6">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white">
                        {selectedNote.title || 'Untitled Note'}
                      </h3>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleToggleFavorite(selectedNote._id)}
                          className={`p-2 ${selectedNote.isFavorite ? 'text-yellow-500' : 'text-gray-400'}`}
                          title={selectedNote.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                        >
                          {selectedNote.isFavorite ? '★ Favorited' : '☆ Favorite'}
                        </button>
                        <button
                          onClick={() => handleToggleArchive(selectedNote._id)}
                          className="p-2 text-gray-400 hover:text-gray-600"
                          title={selectedNote.isArchived ? 'Unarchive note' : 'Archive note'}
                        >
                          {selectedNote.isArchived ? '📁 Unarchive' : '📁 Archive'}
                        </button>
                      </div>
                    </div>

                    {/* Content preview (read-only) */}
                    <div className="w-full h-48 md:h-64 p-3 md:p-4 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white overflow-auto whitespace-pre-wrap">
                      {selectedNote.content ? selectedNote.content : <span className="text-gray-500">No content yet. Upload a file to populate this note.</span>}
                    </div>

                    {/* Upload controls */}
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Upload a file (PDF, DOCX, TXT, MD)</label>
                      <input type="file" accept=".pdf,.docx,.txt,.md" onChange={(e) => handleFileSelect(e)} />
                    <div className="flex gap-2 mt-3">
                        <button
                          onClick={handleUploadReplace}
                          disabled={!selectedFile || uploading}
                          className="px-3 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        >
                          {uploading ? 'Uploading...' : 'Upload & Replace Content'}
                        </button>
                        <button
                          onClick={handleUploadCreate}
                          disabled={!selectedFile || uploading}
                          className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        >
                          {uploading ? 'Uploading...' : 'Upload & Create New Note'}
                        </button>
                        <button
                          onClick={() => setShowClusterModal(true)}
                          className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition text-sm"
                        >
                          📁 Add to Cluster
                        </button>
                      </div>
                    </div>

                    <div className="flex justify-between items-center mt-4">
                      <div className="text-sm text-gray-500">
                        Last edited: {selectedNote.updatedAt ? new Date(selectedNote.updatedAt).toLocaleString() : 'Never'}
                      </div>
                      <div className="flex gap-2 md:gap-3">
                        <button 
                          onClick={() => {
                            setSelectedNote(null);
                            setSummary('');
                          }}
                          className="px-3 md:px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition text-sm md:text-base"
                        >
                          Close
                        </button>
                        <button
                          onClick={() => {
                            if (!selectedNote) return;
                            try {
                              // Create a new PDF document
                              const doc = new jsPDF();
                              let yPosition = 10;
                              const pageHeight = doc.internal.pageSize.getHeight();
                              const margin = 10;
                              const maxWidth = doc.internal.pageSize.getWidth() - 2 * margin;
                              
                              // Add title
                              doc.setFontSize(16);
                              doc.setFont(undefined, 'bold');
                              doc.text(selectedNote.title || 'Untitled Note', margin, yPosition);
                              yPosition += 10;
                              
                              // Add metadata
                              doc.setFontSize(9);
                              doc.setTextColor(100, 100, 100);
                              doc.setFont(undefined, 'normal');
                              doc.text(`Created: ${new Date(selectedNote.createdAt).toLocaleDateString()}`, margin, yPosition);
                              yPosition += 5;
                              
                              if (selectedNote.updatedAt) {
                                doc.text(`Last edited: ${new Date(selectedNote.updatedAt).toLocaleDateString()}`, margin, yPosition);
                                yPosition += 5;
                              }
                              
                              if (selectedNote.tags && selectedNote.tags.length > 0) {
                                doc.text(`Tags: ${selectedNote.tags.join(', ')}`, margin, yPosition);
                                yPosition += 5;
                              }
                              
                              // Add separator line
                              yPosition += 3;
                              doc.setDrawColor(200, 200, 200);
                              doc.line(margin, yPosition, doc.internal.pageSize.getWidth() - margin, yPosition);
                              yPosition += 8;
                              
                              // Add content
                              doc.setFontSize(11);
                              doc.setTextColor(0, 0, 0);
                              doc.setFont(undefined, 'normal');
                              
                              // Split content into multiple lines
                              const contentLines = doc.splitTextToSize(
                                selectedNote.content || 'No content',
                                maxWidth
                              );
                              
                              contentLines.forEach(line => {
                                if (yPosition > pageHeight - 15) {
                                  doc.addPage();
                                  yPosition = 10;
                                }
                                doc.text(line, margin, yPosition);
                                yPosition += 6;
                              });
                              
                              // Save the PDF
                              const filename = (selectedNote.title || 'Note').replace(/[^a-z0-9]/gi, '_').toLowerCase();
                              doc.save(`${filename}_${new Date().toISOString().split('T')[0]}.pdf`);
                              toast.success('Note exported as PDF successfully!');
                            } catch (error) {
                              console.error('PDF export error:', error);
                              toast.error('Failed to export note as PDF');
                            }
                          }}
                          className="px-3 md:px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition text-sm md:text-base"
                        >
                          Export as PDF
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column - AI Tools & Stats */}
              <div className="space-y-4 md:space-y-6">
                {/* AI Summarizer */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4 md:p-6">
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white mb-3 md:mb-4">
                    AI Summarizer
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-3 md:mb-4 text-sm md:text-base">
                    Generate concise summaries of your notes using AI
                  </p>
                  <button
                    onClick={handleSummarize}
                    disabled={!selectedNote || isSummarizing || !selectedNote?.content || !selectedNote?.content.trim()}
                    className="w-full py-2 md:py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold rounded-lg hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm md:text-base"
                  >
                    {isSummarizing ? 'Summarizing...' : 'Summarize Selected Note'}
                  </button>
                  
                  {summary && (
                    <div className="mt-4 md:mt-6">
                      <h4 className="font-bold text-gray-900 dark:text-white mb-2 text-sm md:text-base">Summary:</h4>
                      <div className="p-3 md:p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap text-sm md:text-base">
                          {summary}
                        </p>
                      </div>
                      <div className="flex gap-2 mt-3 md:mt-4">
                        <button 
                          onClick={() => {
                            navigator.clipboard.writeText(summary);
                            toast.success('Summary copied to clipboard!');
                          }}
                          className="flex-1 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition text-sm"
                        >
                          Copy
                        </button>
                        <button 
                          onClick={async () => {
                            try {
                              await notesService.createNote({
                                title: `Summary: ${selectedNote.title}`,
                                content: summary,
                                tags: [...(selectedNote.tags || []), 'summary'],
                                category: selectedNote.category || 'personal'
                              });
                              toast.success('Summary saved as new note!');
                              fetchNotes(); // Refresh the list
                            } catch (error) {
                              toast.error('Failed to save summary as note');
                            }
                          }}
                          className="flex-1 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition text-sm"
                        >
                          Save as Note
                        </button>
                        <button
                          onClick={handleGenerateFlashcards}
                          disabled={isGeneratingFlashcards}
                          className="flex-1 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        >
                          {isGeneratingFlashcards ? 'Generating...' : 'Generate Flashcards'}
                        </button>
                        

                      </div>
                    </div>

                    

                  )}
                </div>

                {/* Quick Stats */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4 md:p-6">
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white mb-3 md:mb-4">
                    Note Stats
                  </h3>
                  <div className="space-y-2 md:space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-300">Total Notes</span>
                      <span className="font-bold text-gray-900 dark:text-white">{stats.total}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-300">Favorites</span>
                      <span className="font-bold text-gray-900 dark:text-white">{stats.favorites}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-300">Archived</span>
                      <span className="font-bold text-gray-900 dark:text-white">{stats.archived}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-300">Last 7 Days</span>
                      <span className="font-bold text-gray-900 dark:text-white">{stats.recent}</span>
                    </div>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4 md:p-6">
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white mb-3 md:mb-4">
                    Quick Actions
                  </h3>
                  <div className="space-y-2">
                    <button
                      onClick={handleCreateNote}
                      className="w-full py-2 px-4 bg-green-600 hover:bg-green-700 text-white rounded-lg transition text-sm md:text-base"
                    >
                      📝 Quick Note
                    </button>
                    {/* Export Notes Section */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Export Notes
                      </label>
                      <select
                        value={exportClusterFilter}
                        onChange={(e) => setExportClusterFilter(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                      >
                        <option value="all">All Notes</option>
                        <option value="summarized">All Summarized Notes</option>
                        {clusters.map(cluster => (
                          <option key={cluster._id} value={cluster._id}>
                            Cluster: {cluster.name}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={handleExportNotes}
                        className="w-full py-2 px-4 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition text-sm md:text-base"
                      >
                        📤 Export as PDF
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default NotesPage;